"use client";
import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

import useMediaQuery from "@/utils/useMediaQuery";
import campusImg from "@/assets/banner/banner-1.webp";

import desktopBanner2 from "@/assets/banner/Homepage/outlook-banner-desktop.webp";
import tabletBanner2 from "@/assets/banner/Homepage/OutlookMobile.png";
import mobileBanner2 from "@/assets/banner/Homepage/OutlookMobile.png";

import desktopBanner4 from "@/assets/banner/Homepage/group-banner.webp";
import tabletBanner4 from "@/assets/banner/Homepage/group-banner.webp";
import mobileBanner4 from "@/assets/banner/Homepage/group-banner.webp";

import desktopBanner6 from "@/assets/banner/Homepage/News18-Bangla-Award.webp";
import tabletBanner6 from "@/assets/banner/Homepage/news18-bangla-award-mobile.webp";
import mobileBanner6 from "@/assets/banner/Homepage/news18-bangla-award-mobile.webp";


import desktopBanner7 from "@/assets/banner/Homepage/et-educational-mobile.webp";
import tabletBanner7 from "@/assets/banner/Homepage/et-education-mobile.webp";
import mobileBanner7 from "@/assets/banner/Homepage/et-education-mobile.webp";

import desktopBanner5 from "@/assets/banner/Homepage/banner-5.png";


const slides = [

  {
    imageDesktop: campusImg,
    imageMobile: campusImg,
    imageTablet: campusImg,
    youtubeId: "TdzDu6241hM",
    title: "Empowering India's Workforce Through",
    highlight: "Skills-Integrated Education",
    // videoUrl: BANNER_VIDEO_URLS[0] || "",
    url: "",
  },
  {
    imageDesktop: desktopBanner2,
    imageMobile: mobileBanner2,
    imageTablet: tabletBanner2,
    title: "",
    highlight: "",
    videoUrl: "",
    url: "",
  },
  {
    imageDesktop: desktopBanner6,
    imageMobile: mobileBanner6,
    imageTablet: tabletBanner6,
    title: "",
    highlight: "",
    videoUrl: "",
    url: "",
  },
  {
    imageDesktop: desktopBanner7,
    imageMobile: mobileBanner7,
    imageTablet: tabletBanner7,
    title: "",
    highlight: "",
    videoUrl: "",
    url: "",
  },
  {
    imageDesktop: desktopBanner4,
    imageMobile: mobileBanner4,
    imageTablet: tabletBanner4,
    title: "Industry-integrated learning across diploma,",
    highlight: "undergraduate and postgraduate programs.",
    stats: [
      {
        value: "16+",
        label: "State of the Art Laboratories for Applied Learning",
      },
      {
        value: "650+",
        label: "Industry & Skilling Partnerships Powering Careers",
      },
      {
        value: "100%",
        label: "Scholarships and Financial Support for Eligible Students",
      },
    ],
    isStatsLayout: true,
  },
  {
    imageDesktop: desktopBanner5,
    imageMobile: desktopBanner5,
    imageTablet: desktopBanner5,
    header: "UGC Recognized | NCVET Aligned | NEP 2020 Ready | Dual Awarding Body",
    title: "Not Just Degrees. Real Careers.",
    subtitle: "Work-Integrated Education with On-the-job Learning",
    description: "Built into Every Program at Medhavi Skills University",
    tagline: "Graduate with a Degree + Industry Experience",
    ctaText: "Start Your Skill Based Journey",
    ctaUrl: "https://application.msu.edu.in/campus-programme-registration",
    videoUrl: "",
    url: "",
    isCustomLayout: true,
  },
];

const Banner = ({ onReady }) => {
  const swiperRef = useRef(null);
  const sectionRef = useRef(null);

  const readyNotified = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [inView, setInView] = useState(true);
  const [loadedVideos, setLoadedVideos] = useState(() => new Set());

  const isTablet = useMediaQuery("(max-width: 660px)");
  const isMobile = useMediaQuery("(max-width: 480px)");
  const isSmallScreen = isMobile || isTablet;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visible = entry.isIntersecting && entry.intersectionRatio > 0.15;
          setInView(visible);
          if (visible && !readyNotified.current) {
            setTimeout(() => {
              if (!readyNotified.current) {
                readyNotified.current = true;
                onReady?.();
              }
            }, 120);
          }
        });
      },
      { threshold: [0, 0.15, 0.5] }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [onReady]);

  useEffect(() => {
    if (!inView) return;
    if (isSmallScreen) return;

    const isFirstSlide = activeIndex === 0;
    const delay = isFirstSlide ? 3000 : 0;

    const timer = setTimeout(() => {
      setLoadedVideos((prev) => {
        const nextSet = new Set(prev);
        [activeIndex, (activeIndex + 1) % slides.length].forEach((i) => {
          // FIX: was `slides[i]?.videoUrl` only, so youtubeId-only slides
          // (like slide 0) never got added and the YouTube branch below
          // could never render.
          if (slides[i]?.videoUrl || slides[i]?.youtubeId) nextSet.add(i);
        });
        return nextSet;
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [activeIndex, inView, isSmallScreen]);

  return (
    <>
      <section ref={sectionRef} className="relative w-full">
        <div className="relative w-full z-10">
          <Swiper
            modules={[Navigation]}
            speed={1200}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(s) => setActiveIndex(s.realIndex)}
            className="w-full soothing-swiper"
          >
            {slides.map((slide, idx) => {
              const isActive = idx === activeIndex;
              const hasText = slide.title || slide.highlight;
              // FIX: `hasVideo` only checked `videoUrl`, so YouTube slides
              // (using `youtubeId`) always fell through to the static
              // <Image> branch and the video never played.
              const hasVideo = Boolean(slide.videoUrl);
              const hasYouTube = Boolean(slide.youtubeId);
              const shouldShowVideo = hasVideo && loadedVideos.has(idx) && !isSmallScreen;
              const shouldShowYouTube = hasYouTube && loadedVideos.has(idx) && !isSmallScreen;
              const imageToUse = isMobile
                ? slide.imageMobile
                : isTablet
                  ? slide.imageTablet
                  : slide.imageDesktop;

              return (
                <SwiperSlide key={idx}>
                  <div className={`relative w-full overflow-hidden h-[530px] sm:h-[530px] md:h-[580px] lg:h-[85vh] xl:h-[81vh]`}>
                    <div className={`absolute inset-0
                  ${idx === 0 ? "opacity-100"
                        : `transition-all duration-[1200ms] ease-out ${isActive ? "opacity-100" : "opacity-0"}`}`}
                    >
                      {shouldShowYouTube ? (
                        <iframe
                          className="w-full h-full object-cover"
                          style={{
                            position: "absolute",
                            top: "-80px",       // push title bar (top) out of view
                            left: "-120px",
                            width: "calc(100% + 160px)",
                            height: "calc(100% + 160px)",
                          }}
                          src={`https://www.youtube-nocookie.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1`}
                          title="Banner video"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          loading="lazy"
                        />
                      )  : (
                        <a
                          href={slide.url || "#"}
                          className={`absolute inset-0 ${idx === 1 || idx === 2 ? "bg-[#004772] " : "bg-black"}`}
                          aria-label={`Banner link ${idx + 1}`}
                          {...(slide.url ? {} : { onClick: (e) => e.preventDefault() })}
                          target="_blank"
                        >
                          <Image
                            src={imageToUse}
                            alt={`Banner ${idx + 1}`}
                            fill
                            sizes="100vw"
                            priority={idx === 0}
                            fetchPriority={idx === 0 ? "high" : "auto"}
                            loading={idx === 0 ? "eager" : "lazy"}
                            className={idx === 1 || idx === 2 ? "object-cover m-auto" : "object-cover"}
                          />
                        </a>
                      )}
                    </div>

                    {hasText && (
                      <div
                        className={`absolute inset-0 bg-black/50
                        ${idx === 0
                            ? "opacity-100"
                            : `transition-opacity duration-[1200ms] ${isActive ? "opacity-100" : "opacity-0"}`
                          }`}
                      />
                    )}

                    {hasText && (
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full max-w-[var(--max-page-width)] mx-auto px-[var(--container-padding)]">
                          {slide.isStatsLayout ? (
                            <div
                              className={`flex flex-col items-center text-center transition-all duration-[1200ms] ease-in-out 
                            ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                            >
                              <p className="text-[22px] md:text-[38px] lg:text-[52px] font-saansBold leading-tight font-black max-w-5xl mb-6 md:mb-12">
                                <span className="text-[#FC9D00]">{slide.title}</span>{" "}
                                <span className="text-white">{slide.highlight}</span>
                              </p>

                              <div className="grid grid-cols-3 gap-0 w-full max-w-6xl px-2">
                                {slide.stats.map((stat, sIdx) => (
                                  <div
                                    key={sIdx}
                                    className={`flex flex-col items-center px-2 md:px-6 ${sIdx !== 2 ? "border-r border-white/20 md:border-white/30" : ""
                                      }`}
                                  >
                                    <span className="text-[24px] sm:text-[28px] md:text-[60px] lg:text-[80px] font-saansBold text-white leading-none mb-1 md:mb-4">
                                      {stat.value}
                                    </span>
                                    <p className="text-white text-[10px] sm:text-[10px] md:text-base lg:text-lg font-medium max-w-[250px] leading-tight md:leading-snug">
                                      {stat.label}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : slide.isCustomLayout ? (
                            <div className={`flex flex-col items-center text-center transition-all duration-[1200ms] ease-in-out ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} pt-10`}>
                              {slide.header && (
                                <p className="text-white text-[10px] md:text-base font-light mb-3 md:mb-4 uppercase tracking-wider opacity-90">
                                  {slide.header}
                                </p>
                              )}
                              <p className="text-[22px] lg:text-[40px] text-[#FC9D00] font-saansBold leading-tight">
                                {slide.title}
                              </p>
                              {slide.subtitle && (
                                <p className="text-[22px] md:text-[22px] text-white font-saansBold leading-tight">
                                  {slide.subtitle}
                                </p>
                              )}
                              {slide.description && (
                                <p className="text-white text-base md:text-2xl font-bold mt-6 md:mt-8 max-w-[90%] md:max-w-4xl">
                                  {slide.description}
                                </p>
                              )}
                              {slide.tagline && (
                                <p className="text-white text-xs md:text-base font-medium opacity-90 mt-2">
                                  {slide.tagline}
                                </p>
                              )}
                              {slide.ctaText && (
                                <a
                                  href={slide.ctaUrl}
                                  className="mt-8 px-8 py-3 md:px-10 md:py-3.5 bg-[#FC9D00] text-white font-saansBold rounded-full text-sm md:text-base hover:bg-[#e08b00] transition-colors inline-block"
                                >
                                  {slide.ctaText}
                                </a>
                              )}
                            </div>
                          ) : idx === 0 ? (
                            <h1
                              className="w-full md:w-[90%] lg:w-[70%] heading_title font-saansBold leading-tight text-white opacity-100"
                            >
                              {slide.title}{" "}
                              {slide.highlight && (
                                <span className="text-[#FC9D00] font-saansBold">{slide.highlight}</span>
                              )}
                            </h1>
                          ) : (
                            <p
                              className={`w-full md:w-[90%] lg:w-[70%] heading_title font-saansBold leading-tight text-white
                            transition-all duration-[1200ms] ease-in
                            ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
                            >
                              {slide.title}{" "}
                              {slide.highlight && (
                                <span className="text-[#FC9D00] font-saansBold">{slide.highlight}</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>

          <div className="absolute bottom-6 md:bottom-14 inset-x-0 z-30 pointer-events-none">
            <div className="w-full max-w-[var(--max-page-width)] mx-auto px-[var(--container-padding)]">
              <div className="flex justify-start">
                <div className="flex gap-3 items-center pointer-events-auto">
                  <button
                    aria-label="Previous slide"
                    onClick={() => swiperRef.current?.slidePrev()}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    aria-label="Next slide"
                    onClick={() => swiperRef.current?.slideNext()}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Banner;